/**
 * Navigation Helper
 * Utility functions to determine the correct landing page based on user permissions
 */

import { getUserPermissions } from '../features/access-management/services/accessManagementService';

/**
 * Module to route mapping
 * Maps module names from Access Management to actual route paths
 */
const MODULE_ROUTES = {
  Dashboard: '/dashboard',
  Customers: '/customers',
  Suppliers: '/suppliers',
  Products: '/products',
  Shopify: '/shopify/products',
  Wishlist: '/wishlist',
  Invoices: '/invoices',
  Offers: '/sales/offers',
  'Sale Orders': '/sales/sale-orders',
  CreditNotes: '/sales/credit-notes',
  'Delivery Notes': '/sales/delivery-notes',
  ReceivedInvoices: '/sales/received-peppol-invoices',
  POS: '/tickets',
  Payments: '/payments',
  Purchases: '/purchases',
  Reports: '/reports',
  Announcements: '/announcements',
  Inventory: '/inventory',
  'Company Settings': '/company-settings',
  'User Settings': '/profile-settings',
  'Store & User Management': '/store-user-settings',
};

/**
 * Default module order (matches sidebar menu order from SideNav.js)
 */
const DEFAULT_MODULE_ORDER = [
  'Dashboard',
  'Customers',
  'Suppliers',
  'Products',
  'Shopify',
  'Wishlist',
  // Sales submenu items
  'Offers',
  'Sale Orders',
  'Delivery Notes',
  'Invoices',
  'ReceivedInvoices',
  'CreditNotes',
  'POS',
  // Rest of menu
  'Purchases',
  'Payments',
  'Reports',
  'Announcements',
  'Inventory',
  'Company Settings',
  'User Settings',
  'Store & User Management',
];

/**
 * Get the first accessible route for a user based on their permissions
 * @param {string} userId - User ID or email
 * @param {string} userRole - User role (SuperAdmin, CompanyAdmin, etc.)
 * @param {Object} userPermissions - User's module permissions (from Redux store)
 * @returns {Promise<string>} First accessible route path
 */
export const getFirstAccessibleRoute = async (userId, userRole, userPermissions = null) => {
  try {
    
    // SuperAdmin and CompanyAdmin have access to everything - default to dashboard
    if (userRole === 'SuperAdmin' || userRole === 'CompanyAdmin') {
      return '/dashboard';
    }
    
    // ✅ Use provided permissions from Redux (avoid duplicate API call)
    let permissions = userPermissions;
    
    // ✅ Only fetch from API if permissions not provided (fallback)
    if (!permissions || Object.keys(permissions).length === 0) {
      const response = await getUserPermissions(userId);
      permissions = response?.payload || response?.permissions || {};
    } else {
    }
    
    
    // Find the first module the user can view
    for (const moduleName of DEFAULT_MODULE_ORDER) {
      const modulePerms = permissions[moduleName];
      
      // Check if user has CanView permission for this module
      if (modulePerms && modulePerms.CanView === true) {
        const route = MODULE_ROUTES[moduleName];
        if (route) {
          return route;
        }
      }
    }
    
    // If no specific permissions found, check if any module has CanView
    const accessibleModules = Object.entries(permissions)
      .filter(([_, perms]) => perms.CanView === true)
      .map(([moduleName, _]) => moduleName);
    
    if (accessibleModules.length > 0) {
      const firstModule = accessibleModules[0];
      const route = MODULE_ROUTES[firstModule] || '/dashboard';
      return route;
    }
    
    // Fallback: if user has no permissions at all, send to unauthorized page
    // (prevents bouncing through a route the user cannot access)

    return '/unauthorized';
    
  } catch (error) {

    // On error, send to unauthorized to avoid breaking the login flow
    return '/unauthorized';
  }
};

/**
 * Check if a user has access to a specific route
 * @param {Object} permissions - User's module permissions
 * @param {string} route - Route path to check
 * @returns {boolean} True if user has access, false otherwise
 */
export const hasRouteAccess = (permissions, route) => {
  // Find module name from route
  const moduleName = Object.entries(MODULE_ROUTES).find(
    ([_, routePath]) => routePath === route
  )?.[0];
  
  if (!moduleName) {
    return true; // Allow access to routes not managed by module permissions
  }
  
  const modulePerms = permissions[moduleName];
  return modulePerms && modulePerms.CanView === true;
};

export default {
  getFirstAccessibleRoute,
  hasRouteAccess,
  MODULE_ROUTES,
};
